/**
 * KYC MANAGEMENT DASHBOARD
 * 
 * Comprehensive admin interface for managing KYC submissions with:
 * - Statistics overview
 * - Submission list with search/filter
 * - Detailed submission viewer
 * - Approve/reject functionality
 * - Batch operations
 * - Audit trail
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { kycAdminService, KYCSubmission, KYCStatistics, KYCFilters } from '../../lib/services/kycAdminService';
import { kycNotificationService } from '../../lib/services/kycNotificationService';
import { notificationService } from '../../lib/notificationService';
import { EmailNotificationService } from '../../lib/services/emailNotificationService';
import { buildFieldRejectEmailReason, resolveFieldRejectNotifyTarget } from '../../lib/kycFieldRejectNotify.js';

const FIELD_REJECT_REQUEST_MS = 20000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}
import { getServerSessionId } from '../../lib/serverSession';
import KYCDocumentViewer from './KYCDocumentViewer';
import RejectionReasonModal from './RejectionReasonModal';
import KYCDeleteConfirmationModal from './KYCDeleteConfirmationModal';
import { logAction, logSearch } from '../../lib/personalActivityLogger';
import { COMPLETE_COUNTRY_LIST } from '../../lib/data/completeCountryList';
import { isKycIdDecryptSentinel, shouldRewriteKycIdHash } from '../../lib/kycAdminUpdateFields.js';

const KYC_EDIT_COUNTRIES = [...COMPLETE_COUNTRY_LIST].sort((a, b) => a.name.localeCompare(b.name));

interface KYCManagementDashboardProps {
  currentUser: any;
}

const KYCManagementDashboard: React.FC<KYCManagementDashboardProps> = ({ currentUser }) => {
  // Helper function to decrypt ID number (simple base64 decoding)
  const decryptIdNumber = (encryptedId: string): string => {
    try {
      if (!encryptedId) return 'N/A';
      // The ID is base64 encoded, so we decode it
      return atob(encryptedId);
    } catch (error) {
      console.error('Error decrypting ID number:', error);
      return 'Error decrypting';
    }
  };
  const originalIdNumberRef = useRef('');

  // State management
  const [submissions, setSubmissions] = useState<KYCSubmission[]>([]);
  const [statistics, setStatistics] = useState<KYCStatistics>({
    total: 0,
    pending: 0,
    completed: 0,
    rejected: 0,
    expired: 0,
    averageProcessingTime: 0,
    completionRate: 0,
    recentSubmissions: 0
  });
  const [selectedSubmission, setSelectedSubmission] = useState<KYCSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminComments, setAdminComments] = useState('');
  const [selectedRejectFields, setSelectedRejectFields] = useState<Set<string>>(new Set());
  const selectedSubmissionRef = useRef<KYCSubmission | null>(null);
  const submissionsRef = useRef<KYCSubmission[]>([]);
  const [processingFields, setProcessingFields] = useState<Set<string>>(new Set());
  const [quickActionSubmission, setQuickActionSubmission] = useState<KYCSubmission | null>(null);
  const [showQuickRejectModal, setShowQuickRejectModal] = useState(false);
  const [processingActions, setProcessingActions] = useState<Set<string>>(new Set());

  // Individual field approval states
  const [fieldApprovals, setFieldApprovals] = useState<{
    [submissionId: string]: {
      personal_info: 'pending' | 'approved' | 'rejected';
      address_info: 'pending' | 'approved' | 'rejected';
      id_document: 'pending' | 'approved' | 'rejected';
      proof_address: 'pending' | 'approved' | 'rejected';
      selfie_verification: 'pending' | 'approved' | 'rejected';
      compliance_info: 'pending' | 'approved' | 'rejected';
    }
  }>({});

  // Filter and search state
  const [filters, setFilters] = useState<KYCFilters>({
    status: 'pending',
    searchTerm: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const itemsPerPage = 50; // Increased from 20 to show more KYCs on first page

  // Tab state for KYC status
  const [activeTab, setActiveTab] = useState<'pending' | 'completed' | 'rejected' | 'delete'>('pending');

  // Modal and action state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comments, setComments] = useState('');
  const [selectedSubmissions, setSelectedSubmissions] = useState<Set<string>>(new Set());

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'username' | 'name' | 'country' | 'status' | 'submitted' | 'confirmed' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Delete KYC state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<KYCSubmission | null>(null);
  const [deletingKYCId, setDeletingKYCId] = useState<string | null>(null);

  // Delete tab state - for searching and deleting any user's KYC
  const [deleteSearchUsername, setDeleteSearchUsername] = useState('');
  const [deleteSearchResult, setDeleteSearchResult] = useState<KYCSubmission | null>(null);
  const [deleteSearchLoading, setDeleteSearchLoading] = useState(false);
  const [deleteSearchError, setDeleteSearchError] = useState<string | null>(null);
  const [deletingUserKYC, setDeletingUserKYC] = useState(false);

  // Edit KYC state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState<KYCSubmission | null>(null);
  const [editFormData, setEditFormData] = useState<{
    first_name: string;
    last_name: string;
    full_legal_name: string;
    id_type: string;
    id_number: string;
    phone_number: string;
    email_address: string;
    street_address: string;
    city: string;
    province: string;
    postal_code: string;
    country_code: string;
    country_name: string;
  }>({
    first_name: '',
    last_name: '',
    full_legal_name: '',
    id_type: '',
    id_number: '',
    phone_number: '',
    email_address: '',
    street_address: '',
    city: '',
    province: '',
    postal_code: '',
    country_code: '',
    country_name: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Unlock KYC for editing state
  const [unlockingKYCId, setUnlockingKYCId] = useState<string | null>(null);

  // Handle unlock KYC for user editing
  const handleUnlockKYC = async (submission: KYCSubmission) => {
    if (!currentUser?.email) {
      alert('Admin email not found');
      return;
    }

    const isCurrentlyEditable = submission.kyc_editable === true;
    const action = isCurrentlyEditable ? 'lock' : 'unlock';

    if (!confirm(`Are you sure you want to ${action} this KYC for user editing?\n\nUser: ${submission.users?.username || submission.email_address}\n\nThis will ${isCurrentlyEditable ? 'prevent' : 'allow'} the user from editing their KYC information.`)) {
      return;
    }

    try {
      setUnlockingKYCId(submission.id);

      const result = await kycAdminService.unlockKYCForEditing(
        submission.id,
        currentUser.email,
        !isCurrentlyEditable // Toggle the current state
      );

      if (result.success) {
        // Update local state
        setSubmissions(prev => prev.map(sub =>
          sub.id === submission.id
            ? { ...sub, kyc_editable: !isCurrentlyEditable }
            : sub
        ));

        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        notification.textContent = `✅ KYC ${isCurrentlyEditable ? 'locked' : 'unlocked'} for user editing!`;
        document.body.appendChild(notification);
        setTimeout(() => document.body.removeChild(notification), 3000);
      } else {
        alert(`Failed to ${action} KYC: ${result.error}`);
      }
    } catch (error) {
      console.error('Error unlocking KYC:', error);
      alert(`Failed to ${action} KYC for editing`);
    } finally {
      setUnlockingKYCId(null);
    }
  };

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load statistics
      const stats = await kycAdminService.getKYCStatistics();
      setStatistics(stats);

      // Load submissions with current filters and documents
      const offset = (currentPage - 1) * itemsPerPage;
      const result = await kycAdminService.getKYCSubmissionsWithDocuments(filters, itemsPerPage, offset);
      setSubmissions(result.submissions);
      setTotalSubmissions(result.total);

    } catch (err) {
      console.error('❌ Error loading KYC data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load KYC data');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => {
    selectedSubmissionRef.current = selectedSubmission;
  }, [selectedSubmission]);

  useEffect(() => {
    submissionsRef.current = submissions;
  }, [submissions]);

  // Initial load and refresh
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Initialize field approvals when submissions are loaded
  useEffect(() => {
    if (submissions.length > 0) {
      const initialApprovals: typeof fieldApprovals = {};
      submissions.forEach(submission => {
        initialApprovals[submission.id] = {
          personal_info: 'pending',
          address_info: 'pending',
          id_document: 'pending',
          proof_address: 'pending',
          selfie_verification: 'pending',
          compliance_info: 'pending'
        };
      });
      setFieldApprovals(initialApprovals);
    }
  }, [submissions]);

  // Handle tab change
  const handleTabChange = useCallback((tab: 'pending' | 'completed' | 'rejected' | 'delete') => {
    setActiveTab(tab);
    if (tab !== 'delete') {
      setCurrentPage(1);
      setFilters(prev => ({
        ...prev,
        status: tab as 'pending' | 'completed' | 'rejected'
      }));
    }
  }, []);

  // Handle search
  const handleSearch = useCallback(() => {
    const q = searchInput.trim();
    setFilters(prev => ({ ...prev, searchTerm: q }));
    setCurrentPage(1);
    if (q.length >= 2) {
      logSearch('kyc', q);
    }
  }, [searchInput]);

  // Handle filter change
  const handleFilterChange = useCallback((newFilters: Partial<KYCFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  // Handle opening edit modal
  const handleEditKYC = useCallback((submission: KYCSubmission) => {
    setEditingSubmission(submission);
    const decryptedId = submission.id_number_encrypted ? decryptIdNumber(submission.id_number_encrypted) : '';
    const idNumber = decryptedId && !isKycIdDecryptSentinel(decryptedId) ? decryptedId : '';
    originalIdNumberRef.current = idNumber;
    const matchedCountry = KYC_EDIT_COUNTRIES.find(item => item.code === String(submission.country_code || '').trim().toUpperCase());
    setEditFormData({
      first_name: submission.first_name || '',
      last_name: submission.last_name || '',
      full_legal_name: submission.full_legal_name || '',
      id_type: submission.id_type || '',
      id_number: idNumber,
      phone_number: submission.phone_number || '',
      email_address: submission.email_address || '',
      street_address: submission.street_address || '',
      city: submission.city || '',
      province: (submission as any).province || '',
      postal_code: submission.postal_code || '',
      country_code: matchedCountry?.code || submission.country_code || '',
      country_name: matchedCountry?.name || submission.country_name || ''
    });
    setShowEditModal(true);
    // v4.7.53 — capture which user's KYC is being edited
    logAction('kyc', 'open_kyc_edit', {
      target_type: 'user',
      target_id: String(submission.user_id),
      target_label: submission.users?.username || submission.email_address || `User ${submission.user_id}`,
      details: { kyc_id: submission.id, kyc_status: submission.kyc_status }
    });
  }, []);

  // Hash ID number for duplicate checking
  const hashIdNumber = async (idNumber: string): Promise<string> => {
    try {
      const normalizedId = idNumber.replace(/[\s-]/g, '').toUpperCase();
      const salt = 'AUREUS_KYC_ID_SALT_2024';
      const saltedId = salt + normalizedId + salt;
      const encoder = new TextEncoder();
      const data = encoder.encode(saltedId);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback hash
      const normalizedId = idNumber.replace(/[\s-]/g, '').toUpperCase();
      let hash = 0;
      for (let i = 0; i < normalizedId.length; i++) {
        hash = ((hash << 5) - hash) + normalizedId.charCodeAt(i);
        hash = hash & hash;
      }
      return Math.abs(hash).toString(16);
    }
  };

  // Handle saving edit
  const handleSaveEdit = useCallback(async () => {
    if (!editingSubmission) return;

    try {
      setSavingEdit(true);

      // v4.8.89 GUARD (Constitution §1/§4): only touch the ID credential fields when the
      // admin actually entered an ID. When the modal's id_number is blank (the decrypt
      // prefill yields '' if id_number_encrypted was already blank/undecodable),
      // btoa('') = '' and hashIdNumber('') = sha256('') — sending those would blank a
      // populated id_number_encrypted / id_number_hash (exact signature that wiped user
      // 437/sergio). So we OMIT both fields entirely when the ID is empty; a real,
      // non-empty ID still encrypts + hashes and updates as before. This is
      // defense-in-depth on top of the backend guard in api/admin/kyc.js.
      const trimmedIdNumber = (editFormData.id_number || '').trim();
      const rewriteIdHash = shouldRewriteKycIdHash(originalIdNumberRef.current, trimmedIdNumber);
      const encryptedIdNumber = rewriteIdHash ? btoa(trimmedIdNumber) : null;
      const idNumberHash = rewriteIdHash ? await hashIdNumber(trimmedIdNumber) : null;

      const updateFields: Record<string, string> = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        id_type: editFormData.id_type,
        phone_number: editFormData.phone_number,
        email_address: editFormData.email_address,
        street_address: editFormData.street_address,
        city: editFormData.city,
        province: editFormData.province,
        postal_code: editFormData.postal_code,
        country_code: editFormData.country_code,
        country_name: editFormData.country_name
      };
      if (rewriteIdHash && encryptedIdNumber && idNumberHash) {
        updateFields.id_number_encrypted = encryptedIdNumber;
        updateFields.id_number_hash = idNumberHash;
      }

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/kyc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'update_kyc_info',
          data: {
            kycId: editingSubmission.id,
            adminEmail: currentUser?.email,
            updateFields
          }
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to update KYC');
      }

      // Update local state with correct field name. Mirror the guard: only overwrite the
      // local id_number_encrypted when a real ID was submitted, so the UI never shows a
      // blank ID over a populated one after an edit that left the ID field untouched.
      setSubmissions(prev => prev.map(sub =>
        sub.id === editingSubmission.id
          ? ({
              ...sub,
              ...editFormData,
              ...(rewriteIdHash && encryptedIdNumber ? { id_number_encrypted: encryptedIdNumber } : {})
            } as any)
          : sub
      ));

      // Also update selectedSubmission if it's the same record
      if (selectedSubmission?.id === editingSubmission.id) {
        setSelectedSubmission(prev => prev ? ({
          ...prev,
          ...editFormData,
          ...(rewriteIdHash && encryptedIdNumber ? { id_number_encrypted: encryptedIdNumber } : {})
        } as any) : null);
      }

      alert('✅ KYC details updated successfully!');
      setShowEditModal(false);
      setEditingSubmission(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error updating KYC:', error);
      alert('❌ Failed to update KYC details: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setSavingEdit(false);
    }
  }, [editingSubmission, editFormData, currentUser, selectedSubmission, loadData]);

  // Define the field approval status type
  type FieldApprovalStatus = 'pending' | 'approved' | 'rejected';
  type FieldName = 'personal_info' | 'address_info' | 'id_document' | 'proof_address' | 'selfie_verification' | 'compliance_info';

  // Send field rejection notification (refs so this is never stale)
  const sendFieldRejectionNotification = useCallback(async (
    submissionId: string,
    field: string,
    rejectionReason: string,
    notifyUserId?: number | null
  ) => {
    try {
      console.log('📧 Sending field rejection notification for:', field);

      const target = resolveFieldRejectNotifyTarget({
        submissionId,
        userId: notifyUserId,
        selectedSubmission: selectedSubmissionRef.current,
        submissions: submissionsRef.current,
      });
      if (!target?.userId) {
        console.error('❌ KYC submission not available for field-reject notify');
        return;
      }

      const fieldLabel = getFieldDisplayName(field);
      const emailReason = buildFieldRejectEmailReason(fieldLabel, rejectionReason);

      const emailResult = await kycNotificationService.sendKYCStatusNotification({
        kycId: submissionId,
        userId: target.userId,
        status: 'rejected',
        rejectionReason: emailReason,
        adminEmail: currentUser?.email || 'admin',
      });
      if (!emailResult.success) {
        console.warn('⚠️ Failed to send KYC field-reject email:', emailResult.error);
      }

      const inAppNotification = await notificationService.createNotification({
        user_id: target.userId,
        type: 'account_update',
        title: '⚠️ KYC Field Requires Attention',
        message: `Your ${fieldLabel} needs to be updated. Reason: ${rejectionReason}. Please resubmit this information.`,
        action_url: '/kyc',
        metadata: {
          kyc_id: submissionId,
          field_name: field,
          rejection_reason: rejectionReason,
          rejection_date: new Date().toISOString()
        }
      });

      if (!inAppNotification) {
        console.warn('⚠️ Failed to create in-app notification for field rejection');
      }

      console.log(`✅ Field rejection notification sent for: ${field}`);
    } catch (error) {
      console.error('❌ Error sending field rejection notification:', error);
    }
  }, [currentUser?.email]);

  // Handle individual field approval
  const handleFieldApproval = useCallback(async (
    submissionId: string,
    field: string,
    status: FieldApprovalStatus,
    rejectionReason?: string,
    notifyUserId?: number | null,
    options?: { notify?: boolean }
  ) => {
    console.log(`🚀 handleFieldApproval called: ${field} -> ${status}`, { submissionId, rejectionReason });

    if (!currentUser?.email) {
      alert('Admin email not found');
      return false;
    }

    const fieldKey = `${submissionId}-${field}`;

    try {
      console.log(`🔄 Processing field approval: ${field} -> ${status}`);

      // Set processing state
      setProcessingFields(prev => new Set(prev).add(fieldKey));

      // Update local state immediately for UI feedback
      setFieldApprovals(prev => {
        const currentSubmission = prev[submissionId] || {
          personal_info: 'pending' as FieldApprovalStatus,
          address_info: 'pending' as FieldApprovalStatus,
          id_document: 'pending' as FieldApprovalStatus,
          proof_address: 'pending' as FieldApprovalStatus,
          selfie_verification: 'pending' as FieldApprovalStatus,
          compliance_info: 'pending' as FieldApprovalStatus
        };

        const newState = {
          ...prev,
          [submissionId]: {
            ...currentSubmission,
            [field as FieldName]: status
          }
        };
        console.log('🔄 Updated fieldApprovals state:', newState);
        return newState;
      });

      // Save to database (only if not pending - pending is just a local state reset)
      if (status !== 'pending') {
        const result = await withTimeout(
          kycAdminService.updateFieldApproval(
            submissionId,
            field,
            status,
            currentUser.email,
            rejectionReason
          ),
          FIELD_REJECT_REQUEST_MS,
          'Connection timed out. Check your internet and try again.'
        );

        if (!result.success) {
          console.error('❌ Database update failed:', result.error);
          // Revert local state on error
          setFieldApprovals(prev => {
            const currentSubmission = prev[submissionId];
            if (!currentSubmission) return prev;

            return {
              ...prev,
              [submissionId]: {
                ...currentSubmission,
                [field as FieldName]: currentSubmission[field as FieldName] || 'pending'
              }
            };
          });
          alert(`Failed to update field approval: ${result.error}`);
          return false;
        }

        console.log(`✅ Field approval updated successfully: ${field} -> ${status}`);

        if (status === 'rejected' && rejectionReason && options?.notify !== false) {
          console.log('📧 Sending rejection notification...');
          await sendFieldRejectionNotification(submissionId, field, rejectionReason, notifyUserId);
        }
        return true;
      }

      console.log(`✅ Field reset to pending (local state only): ${field}`);
      return true;
    } catch (error) {
      console.error('❌ Error updating field approval:', error);
      setFieldApprovals(prev => {
        const currentSubmission = prev[submissionId];
        if (!currentSubmission) return prev;
        return {
          ...prev,
          [submissionId]: {
            ...currentSubmission,
            [field as FieldName]: 'pending'
          }
        };
      });
      const message = error instanceof Error ? error.message : 'Failed to update field approval';
      alert(message);
      return false;
    } finally {
      // Clear processing state
      setProcessingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }
  }, [currentUser?.email, sendFieldRejectionNotification]);

  // Handle field reset to pending
  const handleFieldReset = useCallback(async (submissionId: string, field: string) => {
    console.log(`🔄 Resetting field: ${field} to pending`);
    await handleFieldApproval(submissionId, field, 'pending');
  }, [handleFieldApproval]);

  // Section Reject is a selector only. It does not open a reason window or write KYC.
  const handleFieldReject = useCallback((_submissionId: string, field: string) => {
    setSelectedRejectFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }, []);

  const openFinalRejectModal = useCallback(() => {
    if (selectedRejectFields.size === 0) {
      alert('Select at least one section to reject first (ID, address, selfie, or another section).');
      return;
    }
    setShowRejectModal(true);
  }, [selectedRejectFields]);

  // Get field display name
  const getFieldDisplayName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      personal_info: 'Personal Information',
      address_info: 'Address Information',
      compliance_info: 'Compliance Information',
      id_document: 'ID Document',
      proof_address: 'Proof of Address',
      selfie_verification: 'Selfie Verification'
    };
    return fieldNames[field] || field;
  };

  // Handle KYC approval
  const handleApproveKYC = async (submission: KYCSubmission) => {
    if (!currentUser?.email) {
      alert('User email not found');
      return;
    }

    try {
      setProcessing(true);
      const result = await kycAdminService.approveKYC(
        submission.id,
        currentUser.email,
        adminComments.trim() || undefined
      );

      if (result.success) {
        logAction('kyc', 'kyc_approved', { target_type: 'kyc_submission', target_id: String(submission.id), target_label: submission.full_legal_name || (submission as any).user_email || null });
        setAdminComments('');
        setSelectedSubmission(null);
        loadData();
        alert('KYC approved successfully!');
      } else {
        alert(`Failed to approve KYC: ${result.error}`);
      }
    } catch (error) {
      console.error('Error approving KYC:', error);
      alert('Failed to approve KYC');
    } finally {
      setProcessing(false);
    }
  };

  // Handle KYC rejection
  const handleRejectKYC = async () => {
    if (!selectedSubmission || !currentUser?.email || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    const selectedFields = Array.from(selectedRejectFields);
    if (selectedFields.length === 0) {
      alert('Select at least one section to reject first (ID, address, selfie, or another section).');
      return;
    }

    const submission = selectedSubmission;
    const reason = rejectionReason.trim();
    const notes = adminComments.trim() || undefined;
    const fieldLabels = selectedFields.map((field) => getFieldDisplayName(field)).join(', ');
    const emailReason = buildFieldRejectEmailReason(fieldLabels, reason);

    setShowRejectModal(false);
    setShowDetailModal(false);
    setSelectedSubmission(null);
    setSelectedRejectFields(new Set());
    setRejectionReason('');
    setAdminComments('');

    try {
      setProcessing(true);
      for (const field of selectedFields) {
        const persisted = await withTimeout(
          handleFieldApproval(
            submission.id,
            field,
            'rejected',
            reason,
            submission.user_id,
            { notify: false }
          ),
          FIELD_REJECT_REQUEST_MS,
          'Connection timed out. Check your internet and try again.'
        );
        if (persisted === false) {
          alert(`Failed to mark ${getFieldDisplayName(field)} for rejection.`);
          return;
        }
      }
      const result = await withTimeout(
        kycAdminService.rejectKYC(
          submission.id,
          currentUser.email,
          emailReason,
          notes
        ),
        FIELD_REJECT_REQUEST_MS,
        'Connection timed out. Check your internet and try again.'
      );

      if (result.success) {
        logAction('kyc', 'kyc_rejected', { target_type: 'kyc_submission', target_id: String(submission.id), target_label: submission.full_legal_name || (submission as any).user_email || null, details: { reason } });
        loadData();
        alert('KYC rejected successfully!');
      } else {
        alert(`Failed to reject KYC: ${result.error}`);
      }
    } catch (error) {
      console.error('Error rejecting KYC:', error);
      const message = error instanceof Error ? error.message : 'Failed to reject KYC';
      alert(message);
    } finally {
      setProcessing(false);
    }
  };

  // Handle quick approve action
  const handleQuickApprove = async (submission: KYCSubmission) => {
    if (!currentUser?.email) {
      alert('User email not found');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to approve the KYC submission for ${submission.full_legal_name || `${submission.first_name} ${submission.last_name}`}?`
    );

    if (!confirmed) return;

    try {
      setProcessingActions(prev => new Set(prev).add(submission.id));

      const result = await kycAdminService.quickApproveKYC(
        submission.id,
        currentUser.email,
        'Quick approved from admin dashboard'
      );

      if (result.success) {
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        notification.textContent = '✅ KYC approved successfully!';
        document.body.appendChild(notification);
        setTimeout(() => document.body.removeChild(notification), 3000);

        // Refresh data
        loadData();
      } else {
        alert(`Failed to approve KYC: ${result.error}`);
      }
    } catch (error) {
      console.error('Error approving KYC:', error);
      alert('Failed to approve KYC');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submission.id);
        return newSet;
      });
    }
  };

  // Handle quick reject action
  const handleQuickReject = (submission: KYCSubmission) => {
    setQuickActionSubmission(submission);
    setShowQuickRejectModal(true);
  };

  // Handle quick reject submission
  const handleQuickRejectSubmit = async (rejectionReason: string, adminNotes?: string) => {
    if (!quickActionSubmission || !currentUser?.email) {
      throw new Error('Missing submission or user information');
    }

    try {
      setProcessingActions(prev => new Set(prev).add(quickActionSubmission.id));

      const result = await kycAdminService.quickRejectKYC(
        quickActionSubmission.id,
        currentUser.email,
        rejectionReason,
        adminNotes
      );

      if (result.success) {
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        notification.textContent = '❌ KYC rejected successfully!';
        document.body.appendChild(notification);
        setTimeout(() => document.body.removeChild(notification), 3000);

        // Refresh data
        loadData();
        setQuickActionSubmission(null);
      } else {
        throw new Error(result.error || 'Failed to reject KYC');
      }
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(quickActionSubmission.id);
        return newSet;
      });
    }
  };

  // Handle delete KYC action
  const handleDeleteKYC = (submission: KYCSubmission) => {
    setSubmissionToDelete(submission);
    setShowDeleteModal(true);
  };

  // Handle delete KYC confirmation
  const handleDeleteKYCConfirm = async () => {
    if (!submissionToDelete || !currentUser?.id || !currentUser?.email) {
      alert('Missing required information');
      return;
    }

    try {
      setDeletingKYCId(submissionToDelete.id);

      // Delete the KYC record
      const deleteResult = await kycAdminService.deleteRejectedKYC(
        submissionToDelete.id,
        currentUser.id,
        currentUser.email
      );

      if (!deleteResult.success) {
        throw new Error(deleteResult.message);
      }

      // Send email notification to user
      const emailService = EmailNotificationService.getInstance();
      await emailService.sendKYCDeletionNotification({
        userEmail: submissionToDelete.users?.email || '',
        userName: submissionToDelete.full_legal_name || `${submissionToDelete.first_name} ${submissionToDelete.last_name}`,
        userId: submissionToDelete.user_id,
        reason: 'Your rejected KYC submission has been deleted. You can now resubmit your KYC verification from scratch.'
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = '🗑️ KYC deleted successfully! User notified via email.';
      document.body.appendChild(notification);
      setTimeout(() => document.body.removeChild(notification), 4000);

      // Close modal and refresh data
      setShowDeleteModal(false);
      setSubmissionToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting KYC:', error);
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `❌ Failed to delete KYC: ${error instanceof Error ? error.message : 'Unknown error'}`;
      document.body.appendChild(notification);
      setTimeout(() => document.body.removeChild(notification), 4000);
    } finally {
      setDeletingKYCId(null);
    }
  };

  // Search for user KYC by username (for Delete tab)
  const handleDeleteTabSearch = async () => {
    if (!deleteSearchUsername.trim()) {
      setDeleteSearchError('Please enter a username');
      return;
    }

    try {
      setDeleteSearchLoading(true);
      setDeleteSearchError(null);
      setDeleteSearchResult(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/kyc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'search_user_for_delete',
          data: { searchTerm: deleteSearchUsername.trim() }
        })
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setDeleteSearchError(`User "${deleteSearchUsername.trim()}" not found. Try a different username or partial match.`);
        } else {
          setDeleteSearchError(result.error || 'Error searching for user');
        }
        return;
      }

      const { user: userData, kyc: kycData } = result.data;

      if (!kycData) {
        setDeleteSearchError(`No KYC data found for user "${userData.username}"`);
        return;
      }

      // Map to KYCSubmission type
      setDeleteSearchResult({
        ...kycData,
        users: userData,
        documents: kycData.kyc_documents || []
      } as KYCSubmission);

    } catch (error) {
      console.error('Error searching for user KYC:', error);
      setDeleteSearchError('Error searching for user');
    } finally {
      setDeleteSearchLoading(false);
    }
  };

  // Delete all KYC data for a user (for Delete tab)
  const handleDeleteUserKYC = async () => {
    if (!deleteSearchResult || !currentUser?.id || !currentUser?.email) {
      alert('Missing required information');
      return;
    }

    const confirmMessage = `⚠️ PERMANENT DELETION ⚠️\n\nYou are about to delete ALL KYC data for user "${deleteSearchResult.users?.username}":\n\n• KYC submission record\n• All uploaded documents from storage\n• Document records\n\nThis action CANNOT be undone!\n\nType "DELETE" to confirm:`;

    const confirmation = prompt(confirmMessage);
    if (confirmation !== 'DELETE') {
      alert('Deletion cancelled');
      return;
    }

    try {
      setDeletingUserKYC(true);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/kyc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'delete_user_kyc',
          data: {
            kycId: deleteSearchResult.id,
            userId: deleteSearchResult.user_id,
            adminEmail: currentUser.email,
            username: deleteSearchResult.users?.username
          }
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete KYC data');
      }

      console.log(`✅ Deleted KYC data (${result.data?.documentsDeleted || 0} documents)`);

      // Show success
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `✅ Successfully deleted all KYC data for ${deleteSearchResult.users?.username}`;
      document.body.appendChild(notification);
      setTimeout(() => document.body.removeChild(notification), 4000);

      // Clear search results
      setDeleteSearchResult(null);
      setDeleteSearchUsername('');

      // Refresh main data
      loadData();

    } catch (error) {
      console.error('Error deleting user KYC:', error);
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `❌ Failed to delete KYC: ${error instanceof Error ? error.message : 'Unknown error'}`;
      document.body.appendChild(notification);
      setTimeout(() => document.body.removeChild(notification), 4000);
    } finally {
      setDeletingUserKYC(false);
    }
  };

  // Handle submission selection
  const handleSubmissionSelect = useCallback((submissionId: string, selected: boolean) => {
    setSelectedSubmissions(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(submissionId);
      } else {
        newSet.delete(submissionId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedSubmissions(new Set(submissions.map(s => s.id)));
    } else {
      setSelectedSubmissions(new Set());
    }
  }, [submissions]);

  // Handle approve submission
  const handleApprove = async (submission: KYCSubmission) => {
    if (!currentUser?.email) return;

    try {
      setProcessing(true);
      const result = await kycAdminService.approveKYC(
        submission.id,
        currentUser.email,
        comments.trim() || undefined
      );

      if (result.success) {
        logAction('kyc', 'kyc_approved', { target_type: 'kyc_submission', target_id: String(submission.id), target_label: submission.full_legal_name || (submission as any).user_email || null });
        await loadData();
        setComments('');
        setShowDetailModal(false);
        alert('KYC submission approved successfully!');
      } else {
        alert(`Failed to approve KYC: ${result.error}`);
      }
    } catch (err) {
      console.error('❌ Error approving KYC:', err);
      alert('Failed to approve KYC submission');
    } finally {
      setProcessing(false);
    }
  };

  // Handle batch approve
  const handleBatchApprove = async () => {
    if (selectedSubmissions.size === 0 || !currentUser?.email) return;

    const confirmed = confirm(`Are you sure you want to approve ${selectedSubmissions.size} KYC submissions?`);
    if (!confirmed) return;

    try {
      setProcessing(true);
      const result = await kycAdminService.batchApproveKYC(
        Array.from(selectedSubmissions),
        currentUser.email,
        comments.trim() || undefined
      );

      alert(`Batch approval completed: ${result.successful} successful, ${result.failed} failed`);
      
      if (result.errors.length > 0) {
        console.error('Batch approval errors:', result.errors);
      }

      await loadData();
      setSelectedSubmissions(new Set());
      setComments('');
    } catch (err) {
      console.error('❌ Error in batch approval:', err);
      alert('Failed to perform batch approval');
    } finally {
      setProcessing(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle column sorting
  const handleSort = (column: 'username' | 'name' | 'country' | 'status' | 'submitted' | 'confirmed') => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort submissions based on current sort settings
  const sortedSubmissions = React.useMemo(() => {
    if (!sortColumn) return submissions;

    return [...submissions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'username':
          aValue = a.users?.username?.toLowerCase() || '';
          bValue = b.users?.username?.toLowerCase() || '';
          break;
        case 'name':
          aValue = a.full_legal_name?.toLowerCase() || '';
          bValue = b.full_legal_name?.toLowerCase() || '';
          break;
        case 'country':
          aValue = a.country_name?.toLowerCase() || '';
          bValue = b.country_name?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a.kyc_status?.toLowerCase() || '';
          bValue = b.kyc_status?.toLowerCase() || '';
          break;
        case 'submitted':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'confirmed':
          // For confirmed sorting: completed/rejected KYCs use updated_at, pending KYCs go to bottom
          aValue = (a.kyc_status === 'completed' || a.kyc_status === 'rejected') ? new Date(a.updated_at).getTime() : 0;
          bValue = (b.kyc_status === 'completed' || b.kyc_status === 'rejected') ? new Date(b.updated_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [submissions, sortColumn, sortDirection]);

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'expired': return 'bg-gray-500/20 text-white border-gray-500/30';
      default: return 'bg-gray-500/20 text-white border-gray-500/30';
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalSubmissions / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalSubmissions);

  if (loading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white">Loading KYC submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-yellow-400">🔍 KYC Management</h2>
          <p className="text-white mt-1">
            Review and manage Know Your Customer submissions
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? '🔄' : '↻'} Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400">❌ {error}</p>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Total Submissions</p>
              <p className="text-2xl font-bold text-white">{statistics.total.toLocaleString()}</p>
            </div>
            <div className="text-3xl">📋</div>
          </div>
        </div>

        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-400">{statistics.pending.toLocaleString()}</p>
            </div>
            <div className="text-3xl">⏳</div>
          </div>
        </div>

        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Approved</p>
              <p className="text-2xl font-bold text-green-400">{statistics.completed.toLocaleString()}</p>
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>

        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Avg. Processing</p>
              <p className="text-2xl font-bold text-blue-400">{statistics.averageProcessingTime.toFixed(1)}h</p>
            </div>
            <div className="text-3xl">⚡</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="glass-card p-6">
        <div className="flex flex-col gap-4">
          {/* Search Row */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search by name, email, username..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                />
                <button
                  onClick={handleSearch}
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors"
                >
                  🔍 Search
                </button>
              </div>
            </div>

            {/* Clear Search Button */}
            {filters.searchTerm && (
              <button
                onClick={() => {
                  setSearchInput('');
                  handleFilterChange({ searchTerm: '' });
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              >
                ✕ Clear Search
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-4 items-end">
            {/* Date Range Filter */}
            <div className="flex gap-2 items-center">
              <div>
                <label className="block text-sm text-white mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.dateRange?.start?.split('T')[0] || ''}
                  onChange={(e) => {
                    const startDate = e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined;
                    handleFilterChange({
                      dateRange: startDate || filters.dateRange?.end
                        ? { start: startDate || '', end: filters.dateRange?.end || '' }
                        : undefined
                    });
                  }}
                  className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>
              <span className="text-gray-500 pt-5">to</span>
              <div>
                <label className="block text-sm text-white mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.dateRange?.end?.split('T')[0] || ''}
                  onChange={(e) => {
                    const endDate = e.target.value ? `${e.target.value}T23:59:59.999Z` : undefined;
                    handleFilterChange({
                      dateRange: endDate || filters.dateRange?.start
                        ? { start: filters.dateRange?.start || '', end: endDate || '' }
                        : undefined
                    });
                  }}
                  className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>
            </div>

            {/* Country Filter */}
            <div>
              <label className="block text-sm text-white mb-1">Country</label>
              <select
                value={filters.country || ''}
                onChange={(e) => handleFilterChange({ country: e.target.value || undefined })}
                className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 min-w-[150px]"
              >
                <option value="">All Countries</option>
                <option value="ZA">South Africa</option>
                <option value="NG">Nigeria</option>
                <option value="KE">Kenya</option>
                <option value="GH">Ghana</option>
                <option value="TZ">Tanzania</option>
                <option value="UG">Uganda</option>
                <option value="ZW">Zimbabwe</option>
                <option value="BW">Botswana</option>
                <option value="NA">Namibia</option>
                <option value="MZ">Mozambique</option>
                <option value="ZM">Zambia</option>
                <option value="MW">Malawi</option>
                <option value="RW">Rwanda</option>
                <option value="ET">Ethiopia</option>
                <option value="EG">Egypt</option>
                <option value="MA">Morocco</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="CA">Canada</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="NL">Netherlands</option>
                <option value="AE">UAE</option>
              </select>
            </div>

            {/* Clear All Filters */}
            {(filters.dateRange || filters.country) && (
              <button
                onClick={() => handleFilterChange({ dateRange: undefined, country: undefined })}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              >
                ✕ Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* KYC Status Tabs */}
      <div className="glass-card">
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => handleTabChange('pending')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors border-b-2 ${
              activeTab === 'pending'
                ? 'border-yellow-400 text-yellow-400 bg-gray-800/50'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <span className="text-xl">⏳</span>
              <span>Pending Review</span>
              <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-semibold">
                {statistics.pending}
              </span>
            </span>
          </button>
          <button
            onClick={() => handleTabChange('completed')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors border-b-2 ${
              activeTab === 'completed'
                ? 'border-green-400 text-green-400 bg-gray-800/50'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <span className="text-xl">✅</span>
              <span>Approved</span>
              <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold">
                {statistics.completed}
              </span>
            </span>
          </button>
          <button
            onClick={() => handleTabChange('rejected')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors border-b-2 ${
              activeTab === 'rejected'
                ? 'border-red-400 text-red-400 bg-gray-800/50'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <span className="text-xl">❌</span>
              <span>Rejected</span>
              <span className="ml-2 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-semibold">
                {statistics.rejected}
              </span>
            </span>
          </button>
          <button
            onClick={() => handleTabChange('delete')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors border-b-2 ${
              activeTab === 'delete'
                ? 'border-purple-400 text-purple-400 bg-gray-800/50'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <span className="text-xl">🗑️</span>
              <span>Delete KYC</span>
            </span>
          </button>
        </div>
      </div>

      {/* Delete KYC Tab Content */}
      {activeTab === 'delete' && (
        <div className="glass-card p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white mb-2">🗑️ Delete User KYC Data</h3>
            <p className="text-white text-sm">
              Search for a user by username and permanently delete all their KYC data including uploaded documents.
            </p>
          </div>

          {/* Search Input */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                value={deleteSearchUsername}
                onChange={(e) => setDeleteSearchUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDeleteTabSearch()}
                placeholder="Enter username to search..."
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400"
              />
            </div>
            <button
              onClick={handleDeleteTabSearch}
              disabled={deleteSearchLoading}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {deleteSearchLoading ? '⏳ Searching...' : '🔍 Search'}
            </button>
          </div>

          {/* Search Error */}
          {deleteSearchError && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-400">
              ❌ {deleteSearchError}
            </div>
          )}

          {/* Search Result */}
          {deleteSearchResult && (
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-white mb-4">
                KYC Data Found for: <span className="text-purple-400">{deleteSearchResult.users?.username}</span>
              </h4>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-white text-sm">User ID:</span>
                  <p className="text-white font-medium">{deleteSearchResult.user_id}</p>
                </div>
                <div>
                  <span className="text-white text-sm">Email:</span>
                  <p className="text-white font-medium">{deleteSearchResult.users?.email || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-white text-sm">Full Name:</span>
                  <p className="text-white font-medium">{deleteSearchResult.full_legal_name || `${deleteSearchResult.first_name || ''} ${deleteSearchResult.last_name || ''}`.trim() || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-white text-sm">KYC Status:</span>
                  <p className={`font-medium ${
                    deleteSearchResult.kyc_status === 'completed' ? 'text-green-400' :
                    deleteSearchResult.kyc_status === 'pending' ? 'text-yellow-400' :
                    deleteSearchResult.kyc_status === 'rejected' ? 'text-red-400' : 'text-white'
                  }`}>
                    {deleteSearchResult.kyc_status?.toUpperCase() || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-white text-sm">Submitted:</span>
                  <p className="text-white font-medium">
                    {deleteSearchResult.created_at ? new Date(deleteSearchResult.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-white text-sm">Documents:</span>
                  <p className="text-white font-medium">
                    {(deleteSearchResult as any).kyc_documents?.length || deleteSearchResult.documents?.length || 0} files
                  </p>
                </div>
              </div>

              {/* Warning */}
              <div className="mb-6 p-4 bg-red-900/30 border border-red-500 rounded-lg">
                <p className="text-red-400 font-semibold mb-2">⚠️ WARNING: PERMANENT DELETION</p>
                <p className="text-red-300 text-sm">
                  This will permanently delete:
                </p>
                <ul className="text-red-300 text-sm list-disc list-inside mt-2">
                  <li>KYC submission record</li>
                  <li>All uploaded documents from storage</li>
                  <li>All document records</li>
                </ul>
                <p className="text-red-400 text-sm mt-2 font-semibold">
                  This action CANNOT be undone!
                </p>
              </div>

              {/* Delete Button */}
              <button
                onClick={handleDeleteUserKYC}
                disabled={deletingUserKYC}
                className="w-full px-6 py-4 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition-colors disabled:opacity-50"
              >
                {deletingUserKYC ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  '🗑️ DELETE ALL KYC DATA'
                )}
              </button>
            </div>
          )}

          {/* No Search Yet */}
          {!deleteSearchResult && !deleteSearchError && !deleteSearchLoading && (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">🔍</div>
              <p>Enter a username above to search for their KYC data</p>
            </div>
          )}
        </div>
      )}

      {/* Batch Actions */}
      {activeTab !== 'delete' && selectedSubmissions.size > 0 && (
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-yellow-400 font-medium">
                {selectedSubmissions.size} submission{selectedSubmissions.size !== 1 ? 's' : ''} selected
              </span>
              <input
                type="text"
                placeholder="Optional comments for batch action..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleBatchApprove}
                disabled={processing}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                ✅ Batch Approve
              </button>
              <button
                onClick={() => setSelectedSubmissions(new Set())}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submissions List */}
      {activeTab !== 'delete' && (
      <div className="glass-card">
        {/* Table Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              KYC Submissions ({totalSubmissions.toLocaleString()})
            </h3>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 text-sm text-white">
                <input
                  type="checkbox"
                  checked={selectedSubmissions.size === submissions.length && submissions.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-600 bg-gray-800 text-yellow-400 focus:ring-yellow-400"
                />
                <span>Select All</span>
              </label>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {submissions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">📋</div>
              <p className="text-white text-lg">No KYC submissions found</p>
              <p className="text-gray-500 text-sm mt-2">
                {filters.searchTerm || filters.status !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'KYC submissions will appear here when users submit their verification'
                }
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                    Select
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>User</span>
                      {sortColumn === 'username' && (
                        <span className="text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Name</span>
                      {sortColumn === 'name' && (
                        <span className="text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                    Unique ID hash
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                    onClick={() => handleSort('country')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Country</span>
                      {sortColumn === 'country' && (
                        <span className="text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' && (
                        <span className="text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                    onClick={() => handleSort('submitted')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Submitted</span>
                      {sortColumn === 'submitted' && (
                        <span className="text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                    onClick={() => handleSort('confirmed')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Confirmed</span>
                      {sortColumn === 'confirmed' && (
                        <span className="text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sortedSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedSubmissions.has(submission.id)}
                        onChange={(e) => handleSubmissionSelect(submission.id, e.target.checked)}
                        className="rounded border-gray-600 bg-gray-800 text-yellow-400 focus:ring-yellow-400"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="text-sm font-medium text-white">
                          @{submission.users?.username}
                        </div>
                        <div className="text-sm text-white">
                          {submission.users?.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-white">
                        {submission.full_legal_name || `${submission.first_name} ${submission.last_name}`}
                      </div>
                      <div className="text-sm text-white flex items-center space-x-2">
                        <span>ID: {submission.id_type.replace('_', ' ').toUpperCase()}</span>
                        {submission.documents && submission.documents.length > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            📄 {submission.documents.length} doc{submission.documents.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xs font-mono text-gray-300" title={submission.unique_id_hash || submission.id_number_hash || ''}>
                        {(submission.unique_id_hash || submission.id_number_hash)
                          ? `${String(submission.unique_id_hash || submission.id_number_hash).slice(0, 8)}…${String(submission.unique_id_hash || submission.id_number_hash).slice(-4)}`
                          : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-white">
                        {submission.country_name}
                      </div>
                      <div className="text-sm text-white">
                        {submission.country_code}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full border ${getStatusBadgeColor(submission.kyc_status)}`}>
                        {submission.kyc_status.charAt(0).toUpperCase() + submission.kyc_status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-white">
                      {formatDate(submission.created_at)}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {submission.kyc_status === 'completed' ? (
                        <div>
                          <div className="text-green-400 font-medium">
                            {formatDate(submission.updated_at)}
                          </div>
                          <div className="text-sm text-gray-500">
                            ✅ Approved
                          </div>
                          {submission.kyc_editable && (
                            <div className="text-sm text-cyan-400 mt-1">
                              🔓 Unlocked for editing
                            </div>
                          )}
                        </div>
                      ) : submission.kyc_status === 'rejected' ? (
                        <div>
                          <div className="text-red-400 font-medium">
                            {formatDate(submission.updated_at)}
                          </div>
                          <div className="text-sm text-gray-500">
                            ❌ Rejected
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500 italic">
                          Pending review
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            console.log('🔥 VIEW BUTTON CLICKED, setting selectedSubmission:', submission.id);
                            setSelectedSubmission(submission);
                            setSelectedRejectFields(new Set());
                            setShowDetailModal(true);
                            // v4.7.53 — capture which user's KYC is being viewed
                            logAction('kyc', 'open_kyc_view', {
                              target_type: 'user',
                              target_id: String(submission.user_id),
                              target_label: submission.users?.username || submission.email_address || `User ${submission.user_id}`,
                              details: { kyc_id: submission.id, kyc_status: submission.kyc_status }
                            });

                            // Load existing field approvals only if not already loaded
                            try {
                              const existingApprovals = fieldApprovals[submission.id];
                              if (!existingApprovals) {
                                console.log('📋 Loading field approvals for first time:', submission.id);
                                const approvals = await kycAdminService.getFieldApprovals(submission.id);

                                // Convert Record<string, string> to the expected typed structure
                                const typedApprovals = {
                                  personal_info: (approvals.personal_info || 'pending') as 'pending' | 'approved' | 'rejected',
                                  address_info: (approvals.address_info || 'pending') as 'pending' | 'approved' | 'rejected',
                                  id_document: (approvals.id_document || 'pending') as 'pending' | 'approved' | 'rejected',
                                  proof_address: (approvals.proof_address || 'pending') as 'pending' | 'approved' | 'rejected',
                                  selfie_verification: (approvals.selfie_verification || 'pending') as 'pending' | 'approved' | 'rejected',
                                  compliance_info: (approvals.compliance_info || 'pending') as 'pending' | 'approved' | 'rejected'
                                };

                                setFieldApprovals(prev => ({
                                  ...prev,
                                  [submission.id]: typedApprovals
                                }));
                              } else {
                                console.log('📋 Using existing field approvals:', existingApprovals);
                              }
                            } catch (error) {
                              // Silently handle field approvals error - not critical for viewing KYC
                              console.warn('⚠️ Could not load field approvals (table may not exist):', error);
                            }
                          }}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded transition-colors"
                        >
                          👁️ View
                        </button>
                        {submission.kyc_status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleQuickApprove(submission)}
                              disabled={processingActions.has(submission.id)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center space-x-1"
                            >
                              {processingActions.has(submission.id) ? (
                                <>
                                  <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>...</span>
                                </>
                              ) : (
                                <>
                                  <span>✅</span>
                                  <span>Approve</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleQuickReject(submission)}
                              disabled={processingActions.has(submission.id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center space-x-1"
                            >
                              {processingActions.has(submission.id) ? (
                                <>
                                  <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>...</span>
                                </>
                              ) : (
                                <>
                                  <span>❌</span>
                                  <span>Reject</span>
                                </>
                              )}
                            </button>
                          </>
                        )}
                        {/* Edit button - available for all statuses */}
                        <button
                          onClick={() => handleEditKYC(submission)}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded transition-colors flex items-center space-x-1"
                          title="Edit KYC details"
                        >
                          <span>✏️</span>
                          <span>Edit</span>
                        </button>
                        {/* Unlock/Lock for User Editing button - for completed KYCs */}
                        {submission.kyc_status === 'completed' && (
                          <button
                            onClick={() => handleUnlockKYC(submission)}
                            disabled={unlockingKYCId === submission.id}
                            className={`px-3 py-1 ${submission.kyc_editable ? 'bg-orange-600 hover:bg-orange-500' : 'bg-cyan-600 hover:bg-cyan-500'} text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center space-x-1`}
                            title={submission.kyc_editable ? 'Lock KYC - User can no longer edit' : 'Unlock KYC - Allow user to edit their information'}
                          >
                            {unlockingKYCId === submission.id ? (
                              <>
                                <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>...</span>
                              </>
                            ) : (
                              <>
                                <span>{submission.kyc_editable ? '🔒' : '🔓'}</span>
                                <span>{submission.kyc_editable ? 'Lock' : 'Unlock'}</span>
                              </>
                            )}
                          </button>
                        )}
                        {submission.kyc_status === 'rejected' && (
                          <button
                            onClick={() => handleDeleteKYC(submission)}
                            disabled={deletingKYCId === submission.id}
                            className="px-3 py-1 bg-red-700 hover:bg-red-600 text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center space-x-1"
                            title="Delete this rejected KYC submission"
                          >
                            {deletingKYCId === submission.id ? (
                              <>
                                <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>...</span>
                              </>
                            ) : (
                              <>
                                <span>🗑️</span>
                                <span>Delete</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
            <div className="text-sm text-white">
              Showing {startItem} to {endItem} of {totalSubmissions.toLocaleString()} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 bg-gray-800 text-white rounded">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    🔍 KYC Submission Details
                  </h2>
                  <p className="text-white text-sm">
                    {selectedSubmission.full_legal_name || `${selectedSubmission.first_name} ${selectedSubmission.last_name}`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    console.log('🔥 MODAL CLOSE BUTTON CLICKED');
                    setShowDetailModal(false);
                    setSelectedRejectFields(new Set());
                  }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* User Information */}
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-yellow-400 mb-4">👤 User Information</h3>

                {/* Debug info for missing user data */}
                {!selectedSubmission.users && (
                  <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-red-400">⚠️</span>
                      <span className="text-red-400 font-medium">User Data Missing</span>
                    </div>
                    <p className="text-red-200 text-sm">
                      User ID: {selectedSubmission.user_id} - User record not found or join failed.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white">Username</label>
                    <p className="text-white font-medium">
                      @{selectedSubmission.users?.username || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Email</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.users?.email || selectedSubmission.email_address || 'Not available'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Account Status</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.users?.is_active === true ? '✅ Active' :
                       selectedSubmission.users?.is_active === false ? '❌ Inactive' :
                       '❓ Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Member Since</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.users?.created_at ? formatDate(selectedSubmission.users.created_at) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">User ID</label>
                    <p className="text-white font-medium">#{selectedSubmission.user_id}</p>
                  </div>
                  <div>
                    <label className="text-sm text-white">KYC Record ID</label>
                    <p className="text-white font-medium text-sm">{selectedSubmission.id}</p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div id="kyc-section-personal_info" className="glass-card p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-yellow-400">📋 Personal Information</h3>
                  <div className="flex gap-2">
                    {/* Reset button - only show if field has been approved or rejected */}
                    {fieldApprovals[selectedSubmission.id]?.personal_info && fieldApprovals[selectedSubmission.id]?.personal_info !== 'pending' && (
                      <button
                        onClick={() => handleFieldApproval(selectedSubmission.id, 'personal_info', 'pending')}
                        className="px-2 py-1 rounded text-sm font-medium bg-gray-600 text-gray-300 hover:bg-gray-500 transition-colors"
                        title="Reset to pending"
                      >
                        🔄 Reset
                      </button>
                    )}
                    <button
                      onClick={() => handleFieldApproval(selectedSubmission.id, 'personal_info', 'approved')}
                      disabled={processingFields.has(`${selectedSubmission.id}-personal_info`) || fieldApprovals[selectedSubmission.id]?.personal_info === 'approved'}
                      className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
                        (() => {
                          const status = fieldApprovals[selectedSubmission.id]?.personal_info;
                          const isProcessing = processingFields.has(`${selectedSubmission.id}-personal_info`);

                          if (isProcessing) {
                            return 'bg-yellow-600 text-white animate-pulse';
                          } else if (status === 'approved') {
                            return 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/50 cursor-not-allowed';
                          } else if (status === 'rejected') {
                            return 'bg-green-600/20 text-green-400 hover:bg-green-600/30';
                          } else {
                            return 'bg-green-600/20 text-green-400 hover:bg-green-600/30';
                          }
                        })()
                      }`}
                    >
                      {(() => {
                        const status = fieldApprovals[selectedSubmission.id]?.personal_info;
                        const isProcessing = processingFields.has(`${selectedSubmission.id}-personal_info`);

                        if (isProcessing) {
                          return '⏳ PROCESSING...';
                        } else if (status === 'approved') {
                          return '✅ APPROVED';
                        } else {
                          return '✅ Approve';
                        }
                      })()}
                    </button>
                    <button
                      onClick={() => handleFieldReject(selectedSubmission.id, 'personal_info', 'Personal Information')}
                      disabled={processingFields.has(`${selectedSubmission.id}-personal_info`) || fieldApprovals[selectedSubmission.id]?.personal_info === 'rejected'}
                      className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
                        (() => {
                          const status = fieldApprovals[selectedSubmission.id]?.personal_info;
                          const isProcessing = processingFields.has(`${selectedSubmission.id}-personal_info`);

                          if (isProcessing) {
                            return 'bg-yellow-600 text-white animate-pulse';
                          } else if (status === 'rejected') {
                            return 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/50 cursor-not-allowed';
                          } else if (status === 'approved') {
                            return 'bg-red-600/10 text-red-300 cursor-not-allowed opacity-50';
                          } else if (selectedRejectFields.has('personal_info')) {
                            return 'bg-red-600 text-white border-2 border-red-400';
                          } else {
                            return 'bg-red-600/20 text-red-400 hover:bg-red-600/30';
                          }
                        })()
                      }`}
                    >
                      {(() => {
                        const status = fieldApprovals[selectedSubmission.id]?.personal_info;
                        const isProcessing = processingFields.has(`${selectedSubmission.id}-personal_info`);

                        if (isProcessing) {
                          return '⏳ PROCESSING...';
                        } else if (status === 'rejected') {
                          return '❌ REJECTED';
                        } else if (status === 'approved') {
                          return '❌ Reject (Approved)';
                        } else if (selectedRejectFields.has('personal_info')) {
                          return '❌ Selected';
                        } else {
                          return '❌ Reject';
                        }
                      })()}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white">Full Legal Name</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.full_legal_name || `${selectedSubmission.first_name} ${selectedSubmission.last_name}`}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">ID Type</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.id_type.replace('_', ' ').toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">
                      {selectedSubmission.id_type === 'passport' ? 'Passport Number' : 'ID Number'}
                    </label>
                    <p className="text-white font-medium">
                      {decryptIdNumber(selectedSubmission.id_number_encrypted)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Phone Number</label>
                    <p className="text-white font-medium">{selectedSubmission.phone_number}</p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Email Address</label>
                    <p className="text-white font-medium">{selectedSubmission.email_address}</p>
                  </div>
                </div>
              </div>

              {/* Company Information - Only show if this is a company purchase */}
              {selectedSubmission.is_company_purchase && (
                <div className="glass-card p-4 border-2 border-blue-500/30">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-blue-400">🏢 Company Information</h3>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      Company Purchase
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-white">Company Name</label>
                      <p className="text-white font-medium">
                        {selectedSubmission.company_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-white">Registration Number</label>
                      <p className="text-white font-medium">
                        {selectedSubmission.company_registration_number || 'N/A'}
                      </p>
                    </div>
                    {selectedSubmission.company_registration_document_url && (
                      <div className="col-span-2">
                        <label className="text-sm text-white mb-2 block">Company Registration Document</label>
                        <div className="flex gap-2">
                          <a
                            href={selectedSubmission.company_registration_document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors border border-blue-500/30"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>View Document</span>
                          </a>
                          <a
                            href={selectedSubmission.company_registration_document_url}
                            download={selectedSubmission.company_registration_document_filename || 'company_registration.pdf'}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors border border-green-500/30"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span>Download</span>
                          </a>
                        </div>
                        {selectedSubmission.company_registration_document_filename && (
                          <p className="text-sm text-gray-500 mt-2">
                            {selectedSubmission.company_registration_document_filename}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-blue-900/20 rounded-lg border border-blue-500/30">
                    <p className="text-blue-300 text-sm">
                      <strong>Note:</strong> This user is purchasing shares on behalf of a company. Please verify the company registration document carefully.
                    </p>
                  </div>
                </div>
              )}

              {/* Address Information */}
              <div id="kyc-section-address_info" className="glass-card p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-yellow-400">🏠 Address Information</h3>
                  <div className="flex gap-2">
                    {fieldApprovals[selectedSubmission.id]?.address_info === 'approved' ? (
                      <>
                        <button
                          onClick={() => handleFieldReset(selectedSubmission.id, 'address_info')}
                          className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                        >
                          🔄 Reset
                        </button>
                        <button
                          className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white border-2 border-green-400"
                        >
                          ✅ APPROVED
                        </button>
                        <button
                          onClick={() => handleFieldReject(selectedSubmission.id, 'address_info', 'Address Information')}
                          className="px-3 py-1 rounded text-sm font-medium bg-red-600/10 text-red-400/50 cursor-not-allowed"
                          disabled
                        >
                          ❌ Reject (Approved)
                        </button>
                      </>
                    ) : fieldApprovals[selectedSubmission.id]?.address_info === 'rejected' ? (
                      <>
                        <button
                          onClick={() => handleFieldReset(selectedSubmission.id, 'address_info')}
                          className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                        >
                          🔄 Reset
                        </button>
                        <button
                          onClick={() => handleFieldApproval(selectedSubmission.id, 'address_info', 'approved')}
                          className="px-3 py-1 rounded text-sm font-medium bg-green-600/10 text-green-400/50 cursor-not-allowed"
                          disabled
                        >
                          ✅ Approve (Rejected)
                        </button>
                        <button
                          className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white border-2 border-red-400"
                        >
                          ❌ REJECTED
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleFieldApproval(selectedSubmission.id, 'address_info', 'approved')}
                          className="px-3 py-1 rounded text-sm font-medium bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => handleFieldReject(selectedSubmission.id, 'address_info', 'Address Information')}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${selectedRejectFields.has('address_info') ? 'bg-red-600 text-white border-2 border-red-400' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}
                        >
                          {selectedRejectFields.has('address_info') ? '❌ Selected' : '❌ Reject'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm text-white">Street Address</label>
                    <p className="text-white font-medium">{selectedSubmission.street_address}</p>
                  </div>
                  <div>
                    <label className="text-sm text-white">City</label>
                    <p className="text-white font-medium">{selectedSubmission.city}</p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Postal Code</label>
                    <p className="text-white font-medium">{selectedSubmission.postal_code}</p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Country</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.country_name} ({selectedSubmission.country_code})
                    </p>
                  </div>
                </div>
              </div>

              {/* Compliance Information */}
              <div id="kyc-section-compliance_info" className="glass-card p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-yellow-400">✅ Compliance Information</h3>
                  <div className="flex gap-2">
                    {fieldApprovals[selectedSubmission.id]?.compliance_info === 'approved' ? (
                      <>
                        <button
                          onClick={() => handleFieldReset(selectedSubmission.id, 'compliance_info')}
                          className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                        >
                          🔄 Reset
                        </button>
                        <button
                          className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white border-2 border-green-400"
                        >
                          ✅ APPROVED
                        </button>
                        <button
                          onClick={() => handleFieldReject(selectedSubmission.id, 'compliance_info', 'Compliance Information')}
                          className="px-3 py-1 rounded text-sm font-medium bg-red-600/10 text-red-400/50 cursor-not-allowed"
                          disabled
                        >
                          ❌ Reject (Approved)
                        </button>
                      </>
                    ) : fieldApprovals[selectedSubmission.id]?.compliance_info === 'rejected' ? (
                      <>
                        <button
                          onClick={() => handleFieldReset(selectedSubmission.id, 'compliance_info')}
                          className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                        >
                          🔄 Reset
                        </button>
                        <button
                          onClick={() => handleFieldApproval(selectedSubmission.id, 'compliance_info', 'approved')}
                          className="px-3 py-1 rounded text-sm font-medium bg-green-600/10 text-green-400/50 cursor-not-allowed"
                          disabled
                        >
                          ✅ Approve (Rejected)
                        </button>
                        <button
                          className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white border-2 border-red-400"
                        >
                          ❌ REJECTED
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleFieldApproval(selectedSubmission.id, 'compliance_info', 'approved')}
                          className="px-3 py-1 rounded text-sm font-medium bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => handleFieldReject(selectedSubmission.id, 'compliance_info', 'Compliance Information')}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${selectedRejectFields.has('compliance_info') ? 'bg-red-600 text-white border-2 border-red-400' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}
                        >
                          {selectedRejectFields.has('compliance_info') ? '❌ Selected' : '❌ Reject'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white">Data Consent</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.data_consent_given ? '✅ Given' : '❌ Not Given'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Privacy Policy</label>
                    <p className="text-white font-medium">
                      {selectedSubmission.privacy_policy_accepted ? '✅ Accepted' : '❌ Not Accepted'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">KYC Status</label>
                    <p className="text-white font-medium">
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full border ${getStatusBadgeColor(selectedSubmission.kyc_status)}`}>
                        {selectedSubmission.kyc_status.charAt(0).toUpperCase() + selectedSubmission.kyc_status.slice(1)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-white">Submitted</label>
                    <p className="text-white font-medium">{formatDate(selectedSubmission.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Document Verification */}
              <div className="glass-card p-4">
                <div className="space-y-4">
                  {/* ID Document */}
                  <div id="kyc-section-id_document" className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-yellow-400">🆔 ID Document</h4>
                      <div className="flex gap-2">
                        {fieldApprovals[selectedSubmission.id]?.id_document === 'approved' ? (
                          <>
                            <button
                              onClick={() => handleFieldReset(selectedSubmission.id, 'id_document')}
                              className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                            >
                              🔄 Reset
                            </button>
                            <button
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white border-2 border-green-400"
                            >
                              ✅ APPROVED
                            </button>
                            <button
                              onClick={() => handleFieldReject(selectedSubmission.id, 'id_document', 'ID Document')}
                              className="px-3 py-1 rounded text-sm font-medium bg-red-600/10 text-red-400/50 cursor-not-allowed"
                              disabled
                            >
                              ❌ Reject (Approved)
                            </button>
                          </>
                        ) : fieldApprovals[selectedSubmission.id]?.id_document === 'rejected' ? (
                          <>
                            <button
                              onClick={() => handleFieldReset(selectedSubmission.id, 'id_document')}
                              className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                            >
                              🔄 Reset
                            </button>
                            <button
                              onClick={() => handleFieldApproval(selectedSubmission.id, 'id_document', 'approved')}
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600/10 text-green-400/50 cursor-not-allowed"
                              disabled
                            >
                              ✅ Approve (Rejected)
                            </button>
                            <button
                              className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white border-2 border-red-400"
                            >
                              ❌ REJECTED
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleFieldApproval(selectedSubmission.id, 'id_document', 'approved')}
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                            >
                              ✅ Approve
                            </button>
                            <button
                              onClick={() => handleFieldReject(selectedSubmission.id, 'id_document', 'ID Document')}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${selectedRejectFields.has('id_document') ? 'bg-red-600 text-white border-2 border-red-400' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}
                            >
                              {selectedRejectFields.has('id_document') ? '❌ Selected' : '❌ Reject'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <KYCDocumentViewer
                      kycData={selectedSubmission}
                      onDocumentStatusChange={loadData}
                      currentUser={currentUser}
                      documentType="id_document"
                    />
                  </div>

                  {/* Proof of Address */}
                  <div id="kyc-section-proof_address" className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-yellow-400">🏠 Proof of Address</h4>
                      <div className="flex gap-2">
                        {fieldApprovals[selectedSubmission.id]?.proof_address === 'approved' ? (
                          <>
                            <button
                              onClick={() => handleFieldReset(selectedSubmission.id, 'proof_address')}
                              className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                            >
                              🔄 Reset
                            </button>
                            <button
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white border-2 border-green-400"
                            >
                              ✅ APPROVED
                            </button>
                            <button
                              onClick={() => handleFieldReject(selectedSubmission.id, 'proof_address', 'Proof of Address')}
                              className="px-3 py-1 rounded text-sm font-medium bg-red-600/10 text-red-400/50 cursor-not-allowed"
                              disabled
                            >
                              ❌ Reject (Approved)
                            </button>
                          </>
                        ) : fieldApprovals[selectedSubmission.id]?.proof_address === 'rejected' ? (
                          <>
                            <button
                              onClick={() => handleFieldReset(selectedSubmission.id, 'proof_address')}
                              className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                            >
                              🔄 Reset
                            </button>
                            <button
                              onClick={() => handleFieldApproval(selectedSubmission.id, 'proof_address', 'approved')}
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600/10 text-green-400/50 cursor-not-allowed"
                              disabled
                            >
                              ✅ Approve (Rejected)
                            </button>
                            <button
                              className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white border-2 border-red-400"
                            >
                              ❌ REJECTED
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleFieldApproval(selectedSubmission.id, 'proof_address', 'approved')}
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                            >
                              ✅ Approve
                            </button>
                            <button
                              onClick={() => handleFieldReject(selectedSubmission.id, 'proof_address', 'Proof of Address')}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${selectedRejectFields.has('proof_address') ? 'bg-red-600 text-white border-2 border-red-400' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}
                            >
                              {selectedRejectFields.has('proof_address') ? '❌ Selected' : '❌ Reject'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <KYCDocumentViewer
                      kycData={selectedSubmission}
                      onDocumentStatusChange={loadData}
                      currentUser={currentUser}
                      documentType="proof_of_address"
                    />
                  </div>

                  {/* Selfie Verification */}
                  <div id="kyc-section-selfie_verification" className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-yellow-400">🤳 Selfie Verification</h4>
                      <div className="flex gap-2">
                        {fieldApprovals[selectedSubmission.id]?.selfie_verification === 'approved' ? (
                          <>
                            <button
                              onClick={() => handleFieldReset(selectedSubmission.id, 'selfie_verification')}
                              className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                            >
                              🔄 Reset
                            </button>
                            <button
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white border-2 border-green-400"
                            >
                              ✅ APPROVED
                            </button>
                            <button
                              onClick={() => handleFieldReject(selectedSubmission.id, 'selfie_verification', 'Selfie Verification')}
                              className="px-3 py-1 rounded text-sm font-medium bg-red-600/10 text-red-400/50 cursor-not-allowed"
                              disabled
                            >
                              ❌ Reject (Approved)
                            </button>
                          </>
                        ) : fieldApprovals[selectedSubmission.id]?.selfie_verification === 'rejected' ? (
                          <>
                            <button
                              onClick={() => handleFieldReset(selectedSubmission.id, 'selfie_verification')}
                              className="px-3 py-1 rounded text-sm font-medium bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
                            >
                              🔄 Reset
                            </button>
                            <button
                              onClick={() => handleFieldApproval(selectedSubmission.id, 'selfie_verification', 'approved')}
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600/10 text-green-400/50 cursor-not-allowed"
                              disabled
                            >
                              ✅ Approve (Rejected)
                            </button>
                            <button
                              className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white border-2 border-red-400"
                            >
                              ❌ REJECTED
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleFieldApproval(selectedSubmission.id, 'selfie_verification', 'approved')}
                              className="px-3 py-1 rounded text-sm font-medium bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                            >
                              ✅ Approve
                            </button>
                            <button
                              onClick={() => handleFieldReject(selectedSubmission.id, 'selfie_verification', 'Selfie Verification')}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${selectedRejectFields.has('selfie_verification') ? 'bg-red-600 text-white border-2 border-red-400' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}
                            >
                              {selectedRejectFields.has('selfie_verification') ? '❌ Selected' : '❌ Reject'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <KYCDocumentViewer
                      kycData={selectedSubmission}
                      onDocumentStatusChange={loadData}
                      currentUser={currentUser}
                      documentType="selfie"
                    />
                  </div>
                </div>

                {/* Company Registration Document - Only show if company purchase */}
                {selectedSubmission.is_company_purchase && (
                  <div className="mt-6">
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                      <h4 className="text-blue-400 font-semibold mb-2 flex items-center">
                        <span className="mr-2">🏢</span>
                        Company Registration Document
                      </h4>
                      <p className="text-blue-300 text-sm">
                        Required for company purchase verification
                      </p>
                    </div>
                    <KYCDocumentViewer
                      kycData={selectedSubmission}
                      onDocumentStatusChange={loadData}
                      currentUser={currentUser}
                      documentType="company_registration"
                    />
                  </div>
                )}
              </div>

              {/* KYC Actions */}
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-yellow-400 mb-4">⚡ KYC Actions</h3>

                {selectedSubmission.kyc_status === 'pending' && (
                  <div className="space-y-4">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleApproveKYC(selectedSubmission)}
                        disabled={processing}
                        className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processing ? '⏳ Processing...' : '✅ Approve KYC'}
                      </button>
                      <button
                        onClick={openFinalRejectModal}
                        disabled={processing}
                        className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ❌ Reject KYC
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Admin Comments (Optional)
                      </label>
                      <textarea
                        value={adminComments}
                        onChange={(e) => setAdminComments(e.target.value)}
                        placeholder="Add any comments about this KYC submission..."
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {selectedSubmission.kyc_status !== 'pending' && (
                  <div className="text-center py-4">
                    <p className="text-white">
                      This KYC submission has already been {selectedSubmission.kyc_status}.
                    </p>
                    {selectedSubmission.rejection_reason && (
                      <div className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                        <h4 className="text-red-400 font-medium mb-2">Rejection Reason:</h4>
                        <p className="text-gray-300 text-sm">{selectedSubmission.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Approval Summary — clickable cards scroll to the corresponding section */}
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-yellow-400 mb-2">📊 Approval Summary</h3>
                <p className="text-xs text-gray-400 mb-4">Click any card to jump to that section and view uploaded documents.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: 'personal_info', label: '👤 Personal Info', icon: '👤' },
                    { key: 'address_info', label: '🏠 Address Info', icon: '🏠' },
                    { key: 'compliance_info', label: '✅ Compliance', icon: '✅' },
                    { key: 'id_document', label: '🆔 ID Document', icon: '🆔' },
                    { key: 'proof_address', label: '🏠 Proof Address', icon: '🏠' },
                    { key: 'selfie_verification', label: '🤳 Selfie', icon: '🤳' }
                  ].map(({ key, label, icon }) => {
                    const status = fieldApprovals[selectedSubmission.id]?.[key as keyof typeof fieldApprovals[string]] || 'pending';
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          const el = document.getElementById(`kyc-section-${key}`);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            // Brief highlight flash so the user sees which section they landed on
                            el.classList.add('ring-2', 'ring-yellow-400');
                            setTimeout(() => el.classList.remove('ring-2', 'ring-yellow-400'), 2000);
                          }
                        }}
                        className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-yellow-500/50 hover:bg-gray-700/50 transition-all cursor-pointer text-left w-full"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{icon}</span>
                          <span className="text-sm text-gray-300">{label.split(' ').slice(1).join(' ')}</span>
                        </div>
                        <div className="flex items-center">
                          {status === 'approved' && <span className="text-green-400 text-lg">✅</span>}
                          {status === 'rejected' && <span className="text-red-400 text-lg">❌</span>}
                          {status === 'pending' && <span className="text-yellow-400 text-lg">⏳</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedSubmission.kyc_status === 'pending' && (
                <div className="glass-card p-4">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-4">⚡ Actions</h3>

                  {/* Comments */}
                  <div className="mb-4">
                    <label className="block text-sm text-white mb-2">Comments (Optional)</label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Add any comments for the user..."
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                    />
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => handleApprove(selectedSubmission)}
                      disabled={processing}
                      className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {processing ? '⏳ Processing...' : '✅ Approve KYC'}
                    </button>
                    <button
                      onClick={openFinalRejectModal}
                      disabled={processing}
                      className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      ❌ Reject KYC
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KYC Rejection Modal */}
      {showRejectModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-white mb-4">
              Reject KYC Submission
            </h3>
            <p className="text-gray-300 mb-2">
              User: {selectedSubmission.full_legal_name || `${selectedSubmission.first_name} ${selectedSubmission.last_name}`}
            </p>
            <p className="text-sm text-red-300 mb-4">
              Sections: {Array.from(selectedRejectFields).map((field) => getFieldDisplayName(field)).join(', ')}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejection..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows={4}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Admin Comments (Optional)
                </label>
                <textarea
                  value={adminComments}
                  onChange={(e) => setAdminComments(e.target.value)}
                  placeholder="Additional internal notes..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setAdminComments('');
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectKYC}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors disabled:opacity-50"
              >
                {processing ? 'Rejecting...' : 'Reject KYC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Rejection Modal */}
      <RejectionReasonModal
        isOpen={showQuickRejectModal}
        onClose={() => {
          setShowQuickRejectModal(false);
          setQuickActionSubmission(null);
        }}
        onReject={handleQuickRejectSubmit}
        submissionName={quickActionSubmission ?
          (quickActionSubmission.full_legal_name || `${quickActionSubmission.first_name} ${quickActionSubmission.last_name}`) :
          ''
        }
        isProcessing={quickActionSubmission ? processingActions.has(quickActionSubmission.id) : false}
      />

      {/* Edit KYC Modal */}
      {showEditModal && editingSubmission && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-yellow-400">
                ✏️ Edit KYC Details - @{editingSubmission.users?.username || 'Unknown'}
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSubmission(null);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Personal Information */}
              <div className="glass-card p-4">
                <h4 className="text-lg font-medium text-yellow-400 mb-4">👤 Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">First Name</label>
                    <input
                      type="text"
                      value={editFormData.first_name}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editFormData.last_name}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-1">Full Legal Name</label>
                    <input
                      type="text"
                      value={editFormData.full_legal_name}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, full_legal_name: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">ID Type</label>
                    <select
                      value={editFormData.id_type}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, id_type: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    >
                      <option value="national_id">National ID</option>
                      <option value="passport">Passport</option>
                      <option value="drivers_license">Driver's License</option>
                      <option value="national_id_card">National ID Card</option>
                      <option value="other_government_id">Other Government ID</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">ID Number</label>
                    <input
                      type="text"
                      value={editFormData.id_number}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, id_number: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Phone Number</label>
                    <input
                      type="text"
                      value={editFormData.phone_number}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={editFormData.email_address}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email_address: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="glass-card p-4">
                <h4 className="text-lg font-medium text-yellow-400 mb-4">📍 Address Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={editFormData.street_address}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, street_address: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
                    <input
                      type="text"
                      value={editFormData.city}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Province/State</label>
                    <input
                      type="text"
                      value={editFormData.province}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, province: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Postal Code</label>
                    <input
                      type="text"
                      value={editFormData.postal_code}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                    <select
                      value={editFormData.country_code}
                      onChange={(e) => {
                        const country = KYC_EDIT_COUNTRIES.find(item => item.code === e.target.value);
                        setEditFormData(prev => ({
                          ...prev,
                          country_code: e.target.value,
                          country_name: country?.name || prev.country_name
                        }));
                      }}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    >
                      {!KYC_EDIT_COUNTRIES.some(item => item.code === editFormData.country_code) && (
                        <option value={editFormData.country_code}>
                          {editFormData.country_name || editFormData.country_code || 'Select country'}
                        </option>
                      )}
                      {KYC_EDIT_COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-400">ISO-3 code and name are saved together. Country edits do not change the ID hash.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSubmission(null);
                  }}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-500/50 text-black font-medium rounded-lg transition-colors flex items-center space-x-2"
                >
                  {savingEdit ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete KYC Confirmation Modal */}
      <KYCDeleteConfirmationModal
        isOpen={showDeleteModal}
        kycSubmission={submissionToDelete}
        onConfirm={handleDeleteKYCConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setSubmissionToDelete(null);
        }}
        isLoading={deletingKYCId !== null}
      />
    </div>
  );
};

export default KYCManagementDashboard;
